import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { filterVideos, getHotVideos } from "@/lib/videos";
import {
  buildSearchFilterGroups,
  defaultSearchValues,
  PAGE_SIZE,
} from "@/lib/filterGroups";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SearchBar from "@/components/feature/SearchBar";
import FilterPanel from "@/components/feature/FilterPanel";
import VideoListItem from "@/components/feature/VideoListItem";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import VideoCard from "@/components/feature/VideoCard";

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterOpen, setFilterOpen] = useState(false);

  const keyword = searchParams.get("q") ?? "";
  const values = {
    type: searchParams.get("type") ?? defaultSearchValues.type,
    genre: searchParams.get("genre") ?? defaultSearchValues.genre,
    region: searchParams.get("region") ?? defaultSearchValues.region,
    year: searchParams.get("year") ?? defaultSearchValues.year,
    sort: searchParams.get("sort") ?? defaultSearchValues.sort,
  };
  const page = Math.max(1, Number(searchParams.get("page") ?? "1") || 1);

  usePageMeta({
    title: keyword ? `"${keyword}" 搜索结果 - CC视频` : "搜索影视作品 - CC视频",
    description: keyword
      ? `在CC视频搜索“${keyword}”的相关影视作品、电视剧与综艺内容，支持按类型、地区、年份筛选。`
      : "在CC视频搜索电影、电视剧、综艺等影视作品。",
    keywords: `${keyword ? keyword + "," : ""}CC视频搜索,影视搜索,影视资源`,
  });

  const filtered = useMemo(
    () =>
      filterVideos({
        keyword,
        type: values.type,
        genre: values.genre,
        region: values.region,
        year: values.year,
        sort: values.sort,
      }),
    [keyword, values.type, values.genre, values.region, values.year, values.sort]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const hotVideos = getHotVideos(4);
  const groups = buildSearchFilterGroups();

  const updateParams = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => {
      if (!v || v === defaultSearchValues[k]) {
        next.delete(k);
      } else {
        next.set(k, v);
      }
    });
    setSearchParams(next);
  };

  const handleFilterChange = (key: string, value: string) => {
    updateParams({ [key]: value, page: undefined });
  };

  const handleReset = () => {
    const next = new URLSearchParams();
    if (keyword) next.set("q", keyword);
    setSearchParams(next);
  };

  const handlePageChange = (next: number) => {
    updateParams({ page: next === 1 ? undefined : String(next) });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="max-w-content mx-auto px-4 md:px-6 py-6 md:py-8">
      <Breadcrumb
        items={[{ label: "首页", to: "/" }, { label: "搜索结果" }]}
      />

      <div className="mt-4 mb-6">
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
          {keyword ? `“${keyword}” 的搜索结果` : "搜索影视作品"}
        </h1>
        <div className="mt-4 max-w-2xl">
          <SearchBar key={keyword} size="lg" />
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <aside className="lg:w-64 shrink-0">
          <button
            type="button"
            onClick={() => setFilterOpen((v) => !v)}
            className="lg:hidden w-full h-11 rounded-md bg-background-100 text-foreground-800 text-sm font-medium flex items-center justify-center gap-2 cursor-pointer mb-3 whitespace-nowrap"
          >
            <i className="ri-filter-3-line"></i>
            {filterOpen ? "收起筛选" : "展开筛选"}
            <i className={filterOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
          </button>

          <div
            className={`rounded-lg bg-background-50 border border-background-200 p-5 lg:sticky lg:top-20 ${
              filterOpen ? "block" : "hidden lg:block"
            }`}
          >
            <FilterPanel
              groups={groups}
              values={values}
              onChange={handleFilterChange}
              onReset={handleReset}
            />
          </div>
        </aside>

        <div className="flex-1 min-w-0">
          <div className="mb-4 flex items-center justify-between text-sm text-foreground-500">
            <span>
              共找到{" "}
              <strong className="text-foreground-900">{filtered.length}</strong>{" "}
              条相关结果
            </span>
            {filtered.length > 0 && (
              <span className="hidden sm:inline">
                第 {currentPage} / {totalPages} 页
              </span>
            )}
          </div>

          {pageItems.length === 0 ? (
            <div className="rounded-lg bg-background-50 border border-background-200">
              <EmptyState
                title="没有找到相关结果"
                description="换个关键词试试，或者调整筛选条件，也可以看看下面的热门推荐。"
              />
              <div className="px-5 pb-8">
                <div className="flex flex-col sm:flex-row justify-center gap-3">
                  <Link
                    to="/"
                    className="h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium flex items-center justify-center gap-1.5 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-home-4-line"></i>
                    返回首页
                  </Link>
                  <button
                    type="button"
                    onClick={handleReset}
                    className="h-11 px-6 rounded-md border border-background-300 text-foreground-800 text-sm font-medium flex items-center justify-center gap-1.5 hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-refresh-line"></i>
                    清空筛选
                  </button>
                </div>

                <div className="mt-8">
                  <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">
                    热门推荐
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {hotVideos.map((video) => (
                      <VideoCard key={video.id} video={video} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-4">
                {pageItems.map((video) => (
                  <VideoListItem key={video.id} video={video} />
                ))}
              </div>
              <Pagination
                current={currentPage}
                total={totalPages}
                onChange={handlePageChange}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}